<template>
  <div class="blockcont clearing-house-setup">
    <h4 class="smalltitle">Setup Cleaning House</h4>
    <p>Manage clearing house access and connection settings for this clinic.</p>
    <p v-if="loading" role="status">Loading clearing houses...</p>
    <div v-else-if="loadError" class="alert alert-danger" role="alert">
      {{ loadError }} <button type="button" class="btn btn-default" @click="loadAccess">Retry</button>
    </div>
    <template v-else>
      <p class="help-block">Leave a password or API key blank to keep its saved value. Reload Clinic Setup in EMR after saving.</p>
      <form v-for="provider in providers" :key="provider.key" class="provider-card" @submit.prevent="saveProvider(provider)">
        <fieldset :disabled="saving !== ''">
          <legend>{{ provider.name }}</legend>
          <div class="access-options" role="group" :aria-label="provider.name + ' access'">
            <label class="access-radio">
              <input type="radio" :name="'access-' + clinicId + '-' + provider.key" :value="true" v-model="clearingHouses[provider.key]" @change="clearMessage(provider.key)"> Enable
            </label>
            <label class="access-radio">
              <input type="radio" :name="'access-' + clinicId + '-' + provider.key" :value="false" v-model="clearingHouses[provider.key]" @change="clearMessage(provider.key)"> Disable
            </label>
          </div>
          <div v-if="provider.key !== 'mdsystem'" class="connection-fields">
            <label class="setting-field">
              <span>SFTP username</span>
              <input type="text" v-model="settings[provider.key][provider.userField]" autocomplete="off" maxlength="512" @input="clearMessage(provider.key)">
            </label>
            <label class="setting-field">
              <span>SFTP password</span>
              <input type="password" v-model="settings[provider.key][provider.secretField]" autocomplete="new-password" maxlength="4096"
                     :placeholder="settings[provider.key].hasSecret ? 'Configured — leave blank to keep' : 'Enter password'" @input="clearMessage(provider.key)">
            </label>
          </div>
          <template v-else>
            <label class="setting-field">
              <span>MDSystem integration</span>
              <select v-model="settings.mdsystem.mdsystemIntegrationId" @change="selectIntegration">
                <option :value="null">Please choose</option>
                <option v-if="missingIntegration" :value="settings.mdsystem.mdsystemIntegrationId" disabled>Saved integration is unavailable</option>
                <option v-for="integration in integrations" :key="integration.id" :value="integration.id">{{ integration.name }}{{ integration.active ? '' : ' (inactive)' }}</option>
              </select>
            </label>
            <p v-if="!integrations.length" class="help-block">No MDSystem integration has been provisioned for this clinic.</p>
            <label class="delivery-option">
              <input type="checkbox" v-model="mdsystemSetup.enabled" @change="clearMessage(provider.key)"> Enable outbound DFT delivery
            </label>
            <div class="connection-fields">
              <label class="setting-field">
                <span>HTTPS endpoint</span>
                <input type="text" v-model="mdsystemSetup.url" placeholder="https://" @input="clearMessage(provider.key)">
              </label>
              <label class="setting-field">
                <span>API key header</span>
                <input type="text" v-model="mdsystemSetup.apiKeyHeader" placeholder="api-key" @input="clearMessage(provider.key)">
              </label>
              <label class="setting-field">
                <span>Outbound API key</span>
                <input type="password" v-model="settings.mdsystem.mdsystemApiKey" autocomplete="new-password" maxlength="4096"
                       :placeholder="settings.mdsystem.hasSecret ? 'Configured — leave blank to keep' : 'Enter API key'" @input="clearMessage(provider.key)">
              </label>
            </div>
            <div class="connection-fields">
              <label v-for="doctor in mdsystemSetup.doctors" :key="doctor.code" class="setting-field">
                <span>{{ doctor.name }} ({{ doctor.code }}) — provider number</span>
                <input type="text" v-model="doctor.mdsDoctorNumber" inputmode="numeric" @input="clearMessage(provider.key)">
              </label>
            </div>
          </template>
          <p v-if="errors[provider.key]" class="alert alert-danger" role="alert">{{ errors[provider.key] }}</p>
          <p v-if="saved[provider.key]" class="alert alert-success" role="status">{{ provider.name }} settings saved successfully.</p>
          <button type="submit" class="btn btn-default" :disabled="saving !== ''">
            {{ saving === provider.key ? 'Saving...' : 'Save ' + provider.name }}
          </button>
        </fieldset>
      </form>
    </template>
  </div>
</template>

<script>
import httpClient from '@/client'

const emptySetup = () => ({ enabled: false, url: '', apiKeyHeader: 'api-key', doctors: [] })
const clone = value => JSON.parse(JSON.stringify(value))

export default {
  name: 'TabClearingHouse',
  props: {
    clinicId: { type: [Number, String], required: true }
  },
  data () {
    return {
      providers: [
        { key: 'trizetto', name: 'Trizetto', userField: 'ftpUser', secretField: 'ftpPassword' },
        { key: 'officially', name: 'Officeally', userField: 'officiallyUser', secretField: 'officiallyPassword' },
        { key: 'medusind', name: 'Medusind', userField: 'medusindUser', secretField: 'medusindPassword' },
        { key: 'mdsystem', name: 'MDSystem', userField: 'mdsystemIntegrationId', secretField: 'mdsystemApiKey' }
      ],
      clearingHouses: {}, settings: {}, integrations: [], mdsystemSetup: emptySetup(),
      loading: true, saving: '', loadError: '', errors: {}, saved: {}, requestId: 0
    }
  },
  computed: {
    missingIntegration () {
      const id = this.settings.mdsystem && this.settings.mdsystem.mdsystemIntegrationId
      return !!id && !this.integrations.some(item => item.id === id)
    }
  },
  mounted () {
    this.loadAccess()
  },
  beforeDestroy () {
    this.requestId++
  },
  methods: {
    clearMessage (key) {
      this.$set(this.errors, key, '')
      this.$set(this.saved, key, false)
    },
    selectIntegration () {
      const integration = this.integrations.find(item => item.id === this.settings.mdsystem.mdsystemIntegrationId)
      this.mdsystemSetup = integration ? { ...emptySetup(), ...clone(integration.outbound), doctors: clone(integration.doctors) } : emptySetup()
      this.clearMessage('mdsystem')
    },
    async loadAccess () {
      const requestId = ++this.requestId
      this.loading = true
      this.loadError = ''
      this.saved = {}
      this.errors = {}
      try {
        const [access, configuration] = await Promise.all([
          httpClient.get('/users/clinic-clearing-houses/' + this.clinicId),
          httpClient.get('/users/clinic-clearing-house-settings/' + this.clinicId)
        ])
        if (requestId !== this.requestId) return
        if (access.data.s !== 200) throw new Error(access.data.msg)
        if (configuration.data.s !== 200) throw new Error(configuration.data.msg)
        this.clearingHouses = access.data.data.clearingHouses
        this.settings = configuration.data.data.settings
        this.integrations = configuration.data.data.integrations
        this.selectIntegration()
      } catch (error) {
        if (requestId === this.requestId) this.loadError = error.message || 'Unable to load clearing house settings.'
      } finally {
        if (requestId === this.requestId) this.loading = false
      }
    },
    async saveProvider (provider) {
      if (this.loading || this.saving || this.loadError) return
      const requestId = this.requestId
      const settings = this.settings[provider.key]
      const payload = {
        enabled: this.clearingHouses[provider.key],
        settings: { [provider.userField]: settings[provider.userField], [provider.secretField]: settings[provider.secretField] }
      }
      const setup = provider.key === 'mdsystem' ? clone(this.mdsystemSetup) : null
      if (setup) payload.mdsystemSetup = setup
      this.saving = provider.key
      this.clearMessage(provider.key)
      try {
        const response = await httpClient.put('/users/clinic-clearing-house-settings/' + this.clinicId + '/' + provider.key, payload)
        if (requestId !== this.requestId) return
        if (response.data.s !== 200) throw new Error(response.data.msg)
        settings[provider.secretField] = ''
        settings.hasSecret = response.data.data.hasSecret
        if (setup) {
          const integration = this.integrations.find(item => item.id === settings.mdsystemIntegrationId)
          if (integration) {
            integration.outbound = { enabled: setup.enabled, url: setup.url, apiKeyHeader: setup.apiKeyHeader }
            integration.doctors = clone(setup.doctors)
          }
        }
        this.$set(this.saved, provider.key, true)
      } catch (error) {
        if (requestId === this.requestId) this.$set(this.errors, provider.key, error.message || 'Unable to save clearing house settings.')
      } finally {
        if (requestId === this.requestId) this.saving = ''
      }
    }
  }
}
</script>

<style scoped>
.provider-card {
  margin: 20px 0;
  padding: 18px;
  border: 1px solid #ddd;
  border-radius: 4px;
}
.provider-card fieldset {
  min-width: 0;
  margin: 0;
  padding: 0;
  border: 0;
}
.provider-card legend {
  margin-bottom: 12px;
  border: 0;
  font-size: 17px;
  font-weight: bold;
}
.access-options, .connection-fields {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  margin-bottom: 16px;
}
.access-radio, .delivery-option {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-weight: normal;
}
.access-radio input[type="radio"], .delivery-option input[type="checkbox"] {
  position: static;
  width: auto;
  height: auto;
  margin: 0;
  opacity: 1;
  appearance: auto;
}
.connection-fields .setting-field {
  flex: 1 1 230px;
}
.setting-field {
  display: block;
  min-width: 0;
  margin-bottom: 14px;
  font-weight: normal;
}
.setting-field span {
  display: block;
  margin-bottom: 6px;
}
.setting-field input, .setting-field select {
  box-sizing: border-box;
  width: 100%;
  max-width: 100%;
  height: 38px;
}
.delivery-option {
  margin: 12px 0 18px;
}
.help-block {
  margin: 12px 0;
}
</style>
